import { getTrackExamPageUrl } from '../../utils/RoutingUtils';
import getLang from 'LearningCenterUI/utils/getLang';
import { useCallback } from 'react';
import { addQuestionSubmissionCache, setMostRecentChirpExamAttemptCache, useTrackProgressById } from '../userProgress/userProgressQueries';
import { getPlaylistIdFromUrl } from '../../utils/UrlParamUtils';
import { GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.dfc.new';
import { useSubmitMutation as useSendEmailBreakdownSubmitMutation } from '../../../__generated__/chirp/com/hubspot/academy/assessments/rpc/ExamBreakdownService.hooks';
import { useStartMutation as useStartExamMutation, useResumeMutation as useResumeExamMutation, useSubmitQuestionMutation as useSubmitExamQuestionMutation, useValidateMutation as useValidateExamMutation } from '../../../__generated__/chirp/com/hubspot/academy/assessments/rpc/ExamAttemptService.hooks';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { convertToAcademyLanguage } from '../../utils/languageUtils';
export const useStartExam = () => {
  const [mutate, {
    loading: startExamLoading,
    data: startExamData,
    error: startExamError,
    called: startExamCalled
  }] = useStartExamMutation();
  const startExam = useCallback((trackId, examId, language = getLang()) => {
    const normalizedLanguage = convertToSupportedLanguage(language);
    return mutate({
      variables: {
        examId,
        academyLanguage: normalizedLanguage,
        trackId
      },
      update(__, newData) {
        var _newData$data;
        if ((_newData$data = newData.data) !== null && _newData$data !== void 0 && _newData$data.result) {
          setMostRecentChirpExamAttemptCache(trackId, newData.data.result, normalizedLanguage);
        }
      }
    }).then(mutationResult => {
      var _mutationResult$data;
      return (_mutationResult$data = mutationResult.data) === null || _mutationResult$data === void 0 ? void 0 : _mutationResult$data.result;
    });
  }, [mutate]);
  return [startExam, {
    startExamData,
    startExamLoading,
    startExamError,
    startExamCalled
  }];
};
export const useResumeExam = () => {
  const [mutate, {
    loading: resumeExamLoading,
    data: resumeExamData,
    error: resumeExamError,
    called: resumeExamCalled
  }] = useResumeExamMutation();
  const resumeExam = useCallback((trackId, attemptId, language) => {
    return mutate({
      variables: {
        attemptId
      },
      update(__, newData) {
        var _newData$data2;
        if ((_newData$data2 = newData.data) !== null && _newData$data2 !== void 0 && _newData$data2.result) {
          setMostRecentChirpExamAttemptCache(trackId, newData.data.result, language);
        }
      }
    }).then(mutationResult => {
      var _mutationResult$data2;
      return (_mutationResult$data2 = mutationResult.data) === null || _mutationResult$data2 === void 0 ? void 0 : _mutationResult$data2.result;
    });
  }, [mutate]);
  return [resumeExam, {
    resumeExamData,
    resumeExamLoading,
    resumeExamError,
    resumeExamCalled
  }];
};
export const useSubmitExamQuestion = () => {
  const [mutate, {
    loading: submitExamQuestionLoading,
    data: submitExamQuestionData,
    error: submitExamQuestionError,
    called: submitExamQuestionCalled
  }] = useSubmitExamQuestionMutation();
  const submitExamQuestion = useCallback((trackId, attemptId, questionId, answerIds) => {
    return mutate({
      variables: {
        attemptId,
        questionSubmission: {
          questionId,
          answerIds
        }
      },
      update() {
        addQuestionSubmissionCache(trackId, attemptId, questionId, answerIds);
      }
    }).then(result => result.data);
  }, [mutate]);
  return [submitExamQuestion, {
    submitExamQuestionData,
    submitExamQuestionLoading,
    submitExamQuestionError,
    submitExamQuestionCalled
  }];
};
export const useValidateExam = (trackId, language) => {
  const normalizedLanguage = convertToSupportedLanguage(language);
  const [mutate, {
    loading: validateExamLoading,
    data: validateExamData,
    error: validateExamError,
    called: validateExamCalled
  }] = useValidateExamMutation({
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: normalizedLanguage
      },
      context: {
        timeout: 25000
      }
    }]
  });
  const validateExam = useCallback(attemptId => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      variables: {
        attemptId,
        trackId,
        playlistId
      },
      update(__, newData) {
        var _newData$data3;
        if ((_newData$data3 = newData.data) !== null && _newData$data3 !== void 0 && _newData$data3.result) {
          setMostRecentChirpExamAttemptCache(trackId, newData.data.result, normalizedLanguage);
        }
      }
    }).then(mutationResult => {
      var _mutationResult$data3;
      return (_mutationResult$data3 = mutationResult.data) === null || _mutationResult$data3 === void 0 ? void 0 : _mutationResult$data3.result;
    });
  }, [mutate, trackId, normalizedLanguage]);
  return [validateExam, {
    validateExamData,
    validateExamLoading,
    validateExamError,
    validateExamCalled
  }];
};
export const useSendEmailBreakdown = () => {
  const [mutate, {
    loading: sendEmailBreakdownLoading,
    data: sendEmailBreakdownData,
    error: sendEmailBreakdownError,
    called: sendEmailBreakdownCalled
  }] = useSendEmailBreakdownSubmitMutation();

  // TODO: Once UserProgressBFFChirpService's ExamAttempt is migrated to AcademyLanguage,
  // tighten the `language` parameter from `unknown` to `AcademyLanguage` and remove the
  // runtime coercion via convertToAcademyLanguage.
  // See: https://github.com/HubSpotEngineering/AcademyAchievements/issues/539
  const sendEmailBreakdown = useCallback((attemptId, trackId, language) => {
    return mutate({
      variables: {
        attemptId,
        trackId,
        academyLanguage: convertToAcademyLanguage(language),
        examPath: getTrackExamPageUrl(trackId.toString()).slice(1)
      }
    }).then(result => result.data);
  }, [mutate]);
  return [sendEmailBreakdown, {
    sendEmailBreakdownData,
    sendEmailBreakdownLoading,
    sendEmailBreakdownError,
    sendEmailBreakdownCalled
  }];
};
export const useExamAttempts = trackId => {
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  return {
    examAttempts: trackProgressById ? trackProgressById.examAttempts : undefined,
    trackProgressByIdLoading,
    trackProgressByIdError
  };
};